import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { beatAt, type GuideCues, nextChangeAt, oopsUntil, stepCues, WalkthroughGuideBeat } from "./walkthroughGuidePose";

interface Options {
	enabled: boolean;
	/** Bumped each time a step's tooltip opens; 0 = no step shown yet this tour. */
	shownKey: number;
	textLength: number;
	isLastStep: boolean;
	reducedMotion: boolean;
}

interface StepClock {
	startedAt: number;
	cues: GuideCues;
	oopsEndsAtMs: number | null;
}

interface WalkthroughGuideBeatState {
	beat: WalkthroughGuideBeat;
	oops: () => void;
}

/** Runs the current step's beats on a timer: arrive → talk → point (or the one-time celebration), with oops on demand. */
export const useWalkthroughGuideBeat = ({ enabled, shownKey, textLength, isLastStep, reducedMotion }: Options): WalkthroughGuideBeatState => {
	// The step that celebrated, so a revisited last step rests instead (and a StrictMode re-run of the same step still celebrates).
	const celebratedAtRef = useRef<number | null>(null);
	const [clock, setClock] = useState<StepClock | null>(null);
	const [beat, setBeat] = useState(WalkthroughGuideBeat.Arriving);

	// Layout effects so a new step never paints with the previous step's pose.
	useLayoutEffect(() => {
		if (!enabled || shownKey === 0) {
			celebratedAtRef.current = null;
			setClock(null);
			return;
		}
		const hasCelebrated = celebratedAtRef.current !== null && celebratedAtRef.current !== shownKey;
		const input = { textLength, isLastStep, hasCelebrated, reducedMotion };
		const cues = stepCues(input);
		if (cues.some((cue) => cue.beat === WalkthroughGuideBeat.Done)) celebratedAtRef.current = shownKey;
		setClock({ startedAt: performance.now(), cues, oopsEndsAtMs: null });
	}, [enabled, shownKey, textLength, isLastStep, reducedMotion]);

	useLayoutEffect(() => {
		if (clock === null) return;
		let timer: ReturnType<typeof setTimeout> | null = null;
		const update = () => {
			const elapsed = performance.now() - clock.startedAt;
			setBeat(beatAt(clock.cues, elapsed, clock.oopsEndsAtMs));
			const next = nextChangeAt(clock.cues, elapsed, clock.oopsEndsAtMs);
			timer = next === null ? null : setTimeout(update, next - elapsed);
		};
		update();
		return () => {
			if (timer !== null) clearTimeout(timer);
		};
	}, [clock]);

	const oops = useCallback(() => {
		setClock((current) => (current === null ? current : { ...current, oopsEndsAtMs: oopsUntil(performance.now() - current.startedAt) }));
	}, []);

	return { beat, oops };
};
